import { useAuth } from '../lib/AuthContext';
import { useAdmin } from '../lib/AdminContext';
import { CivicLevel } from '../lib/civicLevelTypes';

export interface EffectiveUser {
  id: string;
  email: string;
  name?: string;
  role: string;
  civicLevel?: string;
  isPreview: boolean;
}

// XP values for each level — must be >= each level's min threshold
const LEVEL_XP_MAP: Record<CivicLevel, number> = {
  'Observer': 70,         // 0–499
  'Explorer': 700,        // 500–1499
  'Sense-Maker': 2000,    // 1500–3999
  'Advocate': 5000,       // 4000–9999
  'Catalyst': 15000,      // 10000–24999
  'Change-Maker': 30000,  // 25000+
};

/**
 * Returns the effective user for the current context.
 * In preview mode, returns a simulated user based on preview settings.
 * Otherwise, returns the real authenticated user.
 */
export function useEffectiveUser() {
  const { user, profile } = useAuth();
  const { viewAsUser, previewLoggedIn, previewCivicLevel } = useAdmin();

  // If preview mode is active
  if (viewAsUser) {
    // If previewing as logged out, return null
    if (!previewLoggedIn) {
      return {
        user: null,
        profile: null,
        isPreviewMode: true,
        isLoggedOut: true,
      };
    }

    // Return simulated user for logged in preview
    const simulatedUser = {
      id: 'preview-user',
      email: 'preview@civicbeat.app',
      aud: 'authenticated',
      role: 'authenticated',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    const simulatedProfile = {
      id: 'preview-user',
      email: 'preview@civicbeat.app',
      name: 'Preview User',
      role: 'user',
      civic_level: previewCivicLevel,
      civicLevel: previewCivicLevel,
      xp: LEVEL_XP_MAP[previewCivicLevel],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    return {
      user: simulatedUser,
      profile: simulatedProfile,
      isPreviewMode: true,
      isLoggedOut: false,
    };
  }

  // Return real user
  return {
    user,
    profile,
    isPreviewMode: false,
    isLoggedOut: !user,
  };
}
